import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { MiPlaceholderArea } from "@miplatform/components";
import { useRouter } from "@miplatform/hooks";
import { SidebarLayout } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { BidFilterSidebar } from "./components/BidFilterSidebar";
import { BidSearchAutocomplete } from "./components/BidSearchAutocomplete";
import { BidsPageHeader } from "./components/BidsPageHeader";
import { useBidFilters } from "./hooks/useBidFilters";
import { useBidSearchParams } from "./hooks/useBidSearchParams";
import { BidFilter } from "./types";
import { useBidsViewModel } from "./useBidsViewModel";

export function Bids() {
  const { query, push } = useRouter();
  const { classes, theme } = useStyles();
  const { isLoading, search, setSearch, tabCounts } = useBidsViewModel();
  const { buildListUrlWithSearchParams } = useBidSearchParams();

  const {
    filters,
    builderOptions,
    communityOptions,
    seriesGroupOptions,
    seriesOptions,
    assigneeOptions,
    planStatusOptions,
    getFilterData,
    resetFilters,
    isLoading: isFiltersLoading,
  } = useBidFilters();

  const handleClearSearchInput = () => {
    setSearch("");
  };

  const handleNavigateToListWithFilters = (filters: BidFilter) => {
    const url = buildListUrlWithSearchParams(filters, search);
    push(url);
  };

  const handleSearchEnterPress = () => {
    if (search.trim().length > 0) {
      push(
        `/estimating/bids/list/search?search=${encodeURIComponent(
          search.trim()
        )}&groupBy=bidRequestId`
      );
    }
  };

  useEffect(() => {
    getFilterData();

    if (query.search) {
      setSearch(query.search as string);
    }
  }, []);

  return (
    <>
      <BidsPageHeader
        isLoading={isLoading}
        setSearch={setSearch}
        tabCounts={tabCounts}
      />
      <SidebarLayout
        sidebarContent={
          <BidFilterSidebar
            builderOptions={builderOptions}
            communityOptions={communityOptions}
            seriesGroupOptions={seriesGroupOptions}
            seriesOptions={seriesOptions}
            assigneeOptions={assigneeOptions}
            planStatusOptions={planStatusOptions}
            filters={filters}
            onApplyFilters={handleNavigateToListWithFilters}
            resetFilters={resetFilters}
            loading={isFiltersLoading || isLoading}
          />
        }
      >
        <Box className={classes.searchContainerDefault}>
          <MiPlaceholderArea
            text=""
            showLogo
            helperText="Search for a bid or bid request."
            containerStyle={{
              backgroundColor: theme.palette.common.white,
              height: "auto",
            }}
            logoStyle={{ height: 64 }}
          />
          <BidSearchAutocomplete
            search={search}
            onSearchChange={setSearch}
            onClearSearch={handleClearSearchInput}
            onEnterPress={handleSearchEnterPress}
          />
        </Box>
      </SidebarLayout>
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  searchContainerDefault: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    alignItems: "center",
    marginTop: theme.spacing(16),
  },
}));
